import React, { useState, useEffect, useContext, useRef } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import UserContext from "../../../contexts/UserContext";
import apiService from "../../../ApiService";
import AIAssistButton from "./AIAssistButton";
import MarkdownRenderer from "./MarkdownRenderer";
import "./SubjectManager.css";
import "./AIFormEnhancements.css";

const SubjectManager = ({ onViewSubject }) => {
  const { user } = useContext(UserContext);
  const userId = user?.userId;

  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({
    subject_name: "",
    description: "",
  });
  const [aiLoading, setAiLoading] = useState(false);
  const descriptionTextareaRef = useRef(null);
  const [deleteStatus, setDeleteStatus] = useState({
    subjectId: null,
    step: 0,
    total: 0,
    label: "",
  });

  useEffect(() => {
    if (userId) {
      fetchSubjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchSubjects = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await apiService.getSubjects(userId, {
        search: searchTerm || undefined,
      });
      setSubjects(response.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSubjects();
  };

  const openCreateModal = () => {
    setEditingSubject(null);
    setFormData({ subject_name: "", description: "" });
    setAiLoading(false);
    setShowModal(true);
  };

  const openEditModal = (subject) => {
    setEditingSubject(subject);
    setFormData({
      subject_name: subject.subject_name,
      description: subject.description || "",
    });
    setAiLoading(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSubject(null);
    setFormData({ subject_name: "", description: "" });
    setAiLoading(false);
  };

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const handleGenerateDescription = async () => {
    if (!formData.subject_name.trim()) {
      alert("Please enter a subject name first");
      return;
    }

    const textarea = descriptionTextareaRef.current;
    const selectionStart = textarea?.selectionStart ?? 0;
    const selectionEnd = textarea?.selectionEnd ?? 0;
    if (textarea && selectionEnd > selectionStart) {
      await handleRewriteDescriptionSelection();
      return;
    }

    setAiLoading(true);
    try {
      console.log("Generating description for subject:", formData.subject_name);

      const response = await apiService.generateSubjectDescription(
        formData.subject_name,
      );

      console.log("AI Response:", response);

      if (response.success) {
        console.log("Setting description:", response.content);
        setFormData((prev) => ({ ...prev, description: response.content }));
      } else {
        alert("Failed to generate description. Please try again.");
      }
    } catch (error) {
      console.error("AI generation error:", error);
      alert("Failed to generate description. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleRewriteDescriptionSelection = async () => {
    const textarea = descriptionTextareaRef.current;
    if (!textarea) return;

    const selectionStart = textarea.selectionStart ?? 0;
    const selectionEnd = textarea.selectionEnd ?? 0;
    if (selectionEnd <= selectionStart) {
      alert("Select the text you want to rewrite.");
      return;
    }

    const selectedText = textarea.value
      .slice(selectionStart, selectionEnd)
      .trim();
    if (!selectedText) {
      alert("Select the text you want to rewrite.");
      return;
    }

    setAiLoading(true);
    try {
      const response = await apiService.rewriteSelectedText(
        selectedText,
        formData.subject_name || "this subject",
      );

      if (response.success) {
        const before = textarea.value.slice(0, selectionStart);
        const after = textarea.value.slice(selectionEnd);
        const nextValue = `${before}${response.content}${after}`;

        setFormData((prev) => ({ ...prev, description: nextValue }));
        setTimeout(() => {
          textarea.focus();
          const caret = selectionStart + response.content.length;
          textarea.setSelectionRange(caret, caret);
        }, 0);
      } else {
        alert("Failed to rewrite selection. Please try again.");
      }
    } catch (error) {
      console.error("AI rewrite error:", error);
      alert("Failed to rewrite selection. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.ctrlKey && e.code === "Space") {
      e.preventDefault();
      handleGenerateDescription();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        user_id: userId,
        subject_name: formData.subject_name,
        description: formData.description || "",
      };

      console.log("Submitting subject with data:", payload);

      if (editingSubject) {
        await apiService.updateSubject(
          userId,
          editingSubject.subject_id,
          payload,
        );
      } else {
        await apiService.createSubject(userId, payload);
      }
      closeModal();
      fetchSubjects();
    } catch (error) {
      console.error("Error saving subject:", error);
      alert("Failed to save subject. Please try again.");
    }
  };

  const handleDelete = async (subjectId, subjectName) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${subjectName}"? This will also delete all related sources, notes, and study logs.`,
      )
    ) {
      try {
        const [sourcesResponse, notesResponse, logsResponse] =
          await Promise.all([
            apiService.getSources(userId, { subject_id: subjectId }),
            apiService.getNotes(userId, { subject_id: subjectId }),
            apiService.getStudyLogs(userId, { subject_id: subjectId }),
          ]);

        const sourcesToDelete = sourcesResponse.sources || [];
        const notesToDelete = notesResponse.notes || [];
        const logsToDelete = logsResponse.study_logs || [];
        const totalDeletes =
          logsToDelete.length +
          notesToDelete.length +
          sourcesToDelete.length +
          1;

        setDeleteStatus({
          subjectId,
          step: 0,
          total: totalDeletes,
          label: "Deleting study logs",
        });

        for (const log of logsToDelete) {
          const logId = log.log_id || log.study_log_id;
          if (logId) {
            await apiService.deleteStudyLog(userId, logId);
            setDeleteStatus((prev) => ({
              ...prev,
              step: prev.step + 1,
              label: "Deleting study logs",
            }));
          }
        }

        for (const note of notesToDelete) {
          if (note.note_id) {
            await apiService.deleteNote(userId, note.note_id);
            setDeleteStatus((prev) => ({
              ...prev,
              step: prev.step + 1,
              label: "Deleting notes",
            }));
          }
        }

        for (const source of sourcesToDelete) {
          if (source.source_id) {
            await apiService.deleteSource(userId, source.source_id);
            setDeleteStatus((prev) => ({
              ...prev,
              step: prev.step + 1,
              label: "Deleting sources",
            }));
          }
        }

        await apiService.deleteSubject(userId, subjectId);
        setDeleteStatus((prev) => ({
          ...prev,
          step: prev.step + 1,
          label: "Deleting subject",
        }));
        fetchSubjects();
      } catch (error) {
        console.error("Error deleting subject:", error);
        alert("Failed to delete subject. Please try again.");
      } finally {
        setDeleteStatus({ subjectId: null, step: 0, total: 0, label: "" });
      }
    }
  };

  return (
    <div className="subject-manager">
      {/* Header Actions */}
      <div className="manager-header">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search subjects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button type="submit" className="search-btn">
            Search
          </button>
        </form>
        <button onClick={openCreateModal} className="create-btn">
          <FontAwesomeIcon icon={faPlus} /> Add Subject
        </button>
      </div>

      {/* Subjects Grid */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading subjects...</p>
        </div>
      ) : subjects.length === 0 ? (
        <div className="empty-subjects">
          <div className="empty-icon">📚</div>
          <h3>No subjects found</h3>
          <p>Create your first subject to start organizing your learning!</p>
          <button onClick={openCreateModal} className="create-btn">
            <FontAwesomeIcon icon={faPlus} /> Create Subject
          </button>
        </div>
      ) : (
        <div className="subjects-grid">
          {subjects.map((subject) => (
            <div key={subject.subject_id} className="subject-card">
              <div
                className="card-content-area"
                onClick={() =>
                  onViewSubject && onViewSubject(subject.subject_id)
                }
                style={{ cursor: onViewSubject ? "pointer" : "default" }}
              >
                <h3>{subject.subject_name}</h3>
                {subject.description && (
                  <MarkdownRenderer
                    content={subject.description}
                    className="card-description"
                    preview
                    previewLength={150}
                  />
                )}
              </div>
              <div className="card-actions">
                {deleteStatus.subjectId === subject.subject_id && (
                  <div className="delete-status">
                    {deleteStatus.label} ({deleteStatus.step}/
                    {deleteStatus.total})
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditModal(subject);
                  }}
                  className="action-btn edit-btn"
                  title="Edit"
                  disabled={deleteStatus.subjectId === subject.subject_id}
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(subject.subject_id, subject.subject_name);
                  }}
                  className="action-btn delete-btn"
                  title="Delete"
                  disabled={deleteStatus.subjectId === subject.subject_id}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal &&
        ReactDOM.createPortal(
          <div className="modal-overlay" onMouseDown={handleOverlayMouseDown}>
            <div
              className="modal-content"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>
                  {editingSubject ? "Edit Subject" : "Create New Subject"}
                </h2>
                <button onClick={closeModal} className="close-btn">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-group">
                  <label htmlFor="subject_name">
                    Subject Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject_name"
                    value={formData.subject_name}
                    onChange={(e) =>
                      setFormData({ ...formData, subject_name: e.target.value })
                    }
                    required
                    maxLength="100"
                    placeholder="e.g., React.js, Data Structures, Machine Learning"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="description">
                    Description
                    <AIAssistButton
                      onGenerate={handleGenerateDescription}
                      loading={aiLoading}
                      size="small"
                      tooltip="Generate description with AI (Ctrl+Space)"
                    />
                  </label>
                  <textarea
                    ref={descriptionTextareaRef}
                    id="description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                    rows="4"
                    placeholder="Brief description of what you're studying... (Ctrl+Space for AI assist)"
                    disabled={aiLoading}
                  ></textarea>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn">
                    {editingSubject ? "Update" : "Create"} Subject
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SubjectManager;
